import apiInstance from "./index";

/**
 * Per-organization hierarchy-level configuration + org-scoped Locations admin.
 * Used by the Build Hierarchy editor on the Organization Admin page.
 * All endpoints below require Super Admin privileges.
 */
// The optional trailing `config` on each method is an axios request config
// (e.g. `{ headers: { "X-Acting-Org-Id": id } }`) so a caller acting on
// another org — an enterprise OWNER building a managed client's hierarchy —
// can scope the request via ActingOrgMiddleware. Omitted for normal
// own-org / super-admin callers, who are unaffected.
export const HierarchyLevelsService = {
  list: (orgId, config = {}) =>
    apiInstance.get(`/organizations/${orgId}/hierarchy-levels`, config),

  /**
   * Bulk replace — body is the ordered array of levels.
   * Server assigns levelNumber from array index (1-based).
   * @param {Array<{name:string, description?:string, isRequired?:boolean, isActive?:boolean}>} levels
   */
  replace: (orgId, levels, config = {}) =>
    apiInstance.put(`/organizations/${orgId}/hierarchy-levels`, levels, config),

  append: (orgId, level, config = {}) =>
    apiInstance.post(`/organizations/${orgId}/hierarchy-levels`, level, config),

  patch: (orgId, levelId, patch, config = {}) =>
    apiInstance.patch(`/organizations/${orgId}/hierarchy-levels/${levelId}`, patch, config),

  remove: (orgId, levelId, config = {}) =>
    apiInstance.delete(`/organizations/${orgId}/hierarchy-levels/${levelId}`, config),
};

export const OrgLocationsAdminService = {
  /**
   * Fetch the location tree for any organization. Requires super-admin.
   * Returns { locationTree: [...] } shape from the existing /api/locations endpoint.
   */
  getTree: (orgId, config = {}) =>
    apiInstance.get("/locations", { params: { organizationId: orgId }, ...config }),

  create: (data, config = {}) =>
    apiInstance.post("/locations", data, config),

  /**
   * Super-admin location update (RLS-bypassed). Body: { name, parentId,
   * hierarchyLevelId, addressId, entityClassId, taxId }.
   * - addressId / entityClassId sentinel: omit/undefined => preserve, 0 => clear, >0 => assign.
   * - taxId sentinel: omit/undefined => preserve, "" (empty) => clear, "value" => set.
   */
  update: (data, config = {}) => {
    const { id, ...body } = data;
    return apiInstance.put(`/super-admin/locations/${id}`, body, config);
  },

  /**
   * Resolve a location's effective address + tax id by walking parentId
   * server-side. Returns { ownAddressId, ownTaxId, effectiveAddressId,
   * effectiveAddressFromLocationId, effectiveAddressFromLocationName,
   * effectiveTaxId, effectiveTaxIdFromLocationId,
   * effectiveTaxIdFromLocationName }. The Build Hierarchy modal computes
   * the same client-side from its in-memory tree; this endpoint backs
   * other consumers that don't have the tree on hand.
   */
  getEffectiveContext: (locationId) =>
    apiInstance.get(`/super-admin/locations/${locationId}/effective-context`),

  remove: (locationId, config = {}) =>
    apiInstance.delete(`/locations/${locationId}`, config),

  reparent: (locationId, parentId, config = {}) =>
    apiInstance.patch(`/locations/${locationId}/parent`, { parentId }, config),
};

/**
 * Org-scoped physical addresses (OrganizationAddresses) — used by the
 * Build Hierarchy editor's per-location address picker.
 */
export const OrgAddressesAdminService = {
  list: (orgId, config = {}) =>
    apiInstance.get(`/super-admin/organizations/${orgId}/addresses`, config),

  /**
   * Create an OrganizationAddress for the given org (super-admin scope).
   * @param {number} orgId
   * @param {{name:string, addressLineOne?:string, addressLineTwo?:string, city?:string, state?:string, country?:string, zipCode?:string}} address
   */
  create: (orgId, address, config = {}) =>
    apiInstance.post(`/super-admin/organizations/${orgId}/addresses`, address, config),

  update: (orgId, addressId, address) =>
    apiInstance.put(
      `/super-admin/organizations/${orgId}/addresses/${addressId}`,
      address
    ),

  remove: (orgId, addressId) =>
    apiInstance.delete(
      `/super-admin/organizations/${orgId}/addresses/${addressId}`
    ),
};

/**
 * Org-scoped Entity Classes (location classification tags) — used by the
 * Build Hierarchy editor's per-location classification picker.
 */
export const OrgEntityClassesAdminService = {
  list: (orgId, config = {}) =>
    apiInstance.get(`/super-admin/organizations/${orgId}/entity-classes`, config),

  /**
   * Create an EntityClass for the given org. Just a name at this point.
   * @param {number} orgId
   * @param {string} name
   */
  create: (orgId, name, config = {}) =>
    apiInstance.post(`/super-admin/organizations/${orgId}/entity-classes`, { name }, config),
};

/**
 * Org-scoped Asset Classes (Build-Hierarchy-style picker, super-admin scope).
 * The API returns a flat list; the client folds it into a nested tree by
 * walking parentId.
 */
export const OrgAssetClassesAdminService = {
  // `config` (optional) lets callers attach a per-request X-Acting-Org-Id
  // header so an enterprise OWNER can read/edit a managed client's asset
  // classes (AllowOwnOrgAccess matches once the acting-org overrides
  // GetOrganizationId to the child).
  list: (orgId, config) =>
    apiInstance.get(`/super-admin/organizations/${orgId}/asset-classes`, config),

  /**
   * Create an AssetClass under an org. parentId optional (null = root).
   * @param {number} orgId
   * @param {{name:string, parentId?:number|null, displayOrder?:number|null}} body
   */
  create: (orgId, body, config) =>
    apiInstance.post(`/super-admin/organizations/${orgId}/asset-classes`, body, config),

  /**
   * Update an AssetClass (rename, optional parent/displayOrder).
   * @param {number} id
   * @param {{name:string, parentId?:number|null, displayOrder?:number|null}} body
   */
  update: (id, body, config) =>
    apiInstance.put(`/super-admin/asset-classes/${id}`, body, config),

  /**
   * Reparent (move) an AssetClass.
   * @param {number} id
   * @param {number|null} parentId
   */
  reparent: (id, parentId, config) =>
    apiInstance.patch(`/super-admin/asset-classes/${id}/parent`, { parentId }, config),

  /**
   * Soft-delete an AssetClass (sets isActive=false; keeps Asset references intact).
   */
  remove: (id) =>
    apiInstance.delete(`/super-admin/asset-classes/${id}`),
};

export default HierarchyLevelsService;
